import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  Close as CloseIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import ChangePasswordDialog from './ChangePasswordDialog';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';

const SECRET_KEY = 'tabtrips_hotel_secret';
const TOKEN_COOKIE_KEY = 'token';

function decryptData(ciphertext) {
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decrypted);
  } catch {
    return null;
  }
}

function removeToken() {
  Cookies.remove(TOKEN_COOKIE_KEY);
}

const ProfileDialog = ({ open, onClose, onLogout }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState({
    email: ''
  });
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [selectedHostId, setSelectedHostId] = useState(localStorage.getItem('selectedHostId') || '');

  useEffect(() => {
    if (open) {
      // Load hotel data from localStorage
      const hotelListStr = localStorage.getItem('hotelsList');
      if (hotelListStr) {
        const hotelList = decryptData(hotelListStr);
        const selectedHotelId = localStorage.getItem('selectedHotelId');
        const hotelData = selectedHotelId 
          ? hotelList.find(hotel => hotel.hotel_id.toString() === selectedHotelId)
          : hotelList[0];
        
        if (hotelData) {
          setFormData({
            email: hotelData.email || ''
          });
        }
      }
    }
  }, [open]);

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      removeToken();
      window.location.href = '/login';
    }
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(0,0,0,0.05)',
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          color: '#2E4057',
          fontWeight: 600,
          pb: 1,
          borderBottom: '1px solid rgba(0,0,0,0.08)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ color: '#FF7700' }} />
            Profile Settings
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 3 }}>
          <Grid container spacing={3}>
            {/* Password Management Card */}
            <Grid item xs={12}>
              <Card sx={{ 
                borderRadius: 2, 
                border: '1px solid rgba(255,119,0,0.2)',
                bgcolor: 'rgba(255,119,0,0.02)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'rgba(255,119,0,0.4)',
                  bgcolor: 'rgba(255,119,0,0.05)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(255,119,0,0.15)'
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LockIcon sx={{ color: '#FF7700', mr: 1, fontSize: '1.5rem' }} />
                    <Typography variant="h6" sx={{ color: '#2E4057', fontWeight: 600 }}>
                      Password Management
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" sx={{ 
                    color: 'text.secondary', 
                    mb: 3,
                    lineHeight: 1.6
                  }}>
                    Keep your account secure by regularly updating your password. 
                    Use a password with at least 6 characters.
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      component={motion.button}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      variant="contained"
                      startIcon={<LockIcon />}
                      onClick={() => setChangePasswordDialogOpen(true)}
                      sx={{ 
                        borderRadius: 2,
                        px: 3,
                        py: 1.2,
                        background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                        boxShadow: '0 4px 12px rgba(255,119,0,0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                          boxShadow: '0 6px 16px rgba(255,119,0,0.4)',
                        }
                      }}
                    >
                      Change Password
                    </Button>
                    
                    <Button
                      component={motion.button}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      variant="outlined"
                      startIcon={<SecurityIcon />}
                      onClick={() => setChangePasswordDialogOpen(true)}
                      sx={{ 
                        borderRadius: 2,
                        px: 3,
                        py: 1.2,
                        borderColor: '#FF7700',
                        color: '#FF7700',
                        '&:hover': {
                          borderColor: '#E56900',
                          backgroundColor: 'rgba(255,119,0,0.05)',
                        }
                      }}
                    >
                      Reset Password
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Account Information Card */}
            <Grid item xs={12}>
              <Card sx={{ 
                borderRadius: 2, 
                border: '1px solid rgba(46,64,87,0.2)',
                bgcolor: 'rgba(46,64,87,0.02)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'rgba(46,64,87,0.4)',
                  bgcolor: 'rgba(46,64,87,0.05)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(46,64,87,0.15)'
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <EmailIcon sx={{ color: '#2E4057', mr: 1, fontSize: '1.5rem' }} />
                    <Typography variant="h6" sx={{ color: '#2E4057', fontWeight: 600 }}>
                      Account Information
                    </Typography>
                  </Box>
                  
                  <TextField
                    fullWidth
                    label="Email Address"
                    value={formData.email}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: '#2E4057' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        color: 'text.secondary'
                      }
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Security Tips Card */}
            <Grid item xs={12}>
              <Card sx={{ 
                borderRadius: 2, 
                border: '1px solid rgba(76,175,80,0.2)',
                bgcolor: 'rgba(76,175,80,0.02)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'rgba(76,175,80,0.4)',
                  bgcolor: 'rgba(76,175,80,0.05)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(76,175,80,0.15)'
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SecurityIcon sx={{ color: '#4CAF50', mr: 1, fontSize: '1.5rem' }} />
                    <Typography variant="h6" sx={{ color: '#2E4057', fontWeight: 600 }}>
                      Security Tips
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {[
                      'Use a unique password that you don\'t use elsewhere',
                      'Make your password at least 6 characters long',
                      'Never share your password with anyone',
                      'Change your password regularly for better security'
                    ].map((tip, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ 
                          width: 6, 
                          height: 6, 
                          borderRadius: '50%', 
                          bgcolor: '#4CAF50',
                          flexShrink: 0
                        }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {tip}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Logout Button */}
            <Grid item xs={12}>
              <Button
                component={motion.button}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                fullWidth
                variant="contained"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  background: 'linear-gradient(135deg, #F44336 0%, #E53935 100%)',
                  boxShadow: '0 4px 12px rgba(244,67,54,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #F44336 0%, #E53935 100%)',
                    boxShadow: '0 6px 16px rgba(244,67,54,0.4)',
                  }
                }}
              >
                Logout
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={changePasswordDialogOpen}
        onClose={() => setChangePasswordDialogOpen(false)}
        userEmail={formData.email}
        userId={selectedHostId}
      />
    </>
  );
};

export default ProfileDialog;
















